import React from 'react';

import { sameDestination } from './tracking-link';
import {
  fetchTrackingLinks,
  retargetTrackingLink,
  upsertTrackingLink,
  type TrackingLink,
  type TrackingLinkDestination,
  type TrackingLinkKind,
} from './tracking-links-api';
import { invalidateBrandLinks } from './useBrandLinks';

/** What one asset version needs to read or make its tracking link (see `tracking-links-api.ts`). */
export interface UseTrackingLinkInput {
  /** The brand (creatives tenant). None: `no-brand`. */
  tenantId?: number;
  /** The asset (video ad, banner kit, mini-game, HTML5 banner, or the landing page for its own link). */
  creativeId: string;
  /** The asset version (a landing page's own link: `page`). */
  versionKey: string;
  kind: TrackingLinkKind;
  /** The link's display name, written when the link is made. */
  name: string;
}

/**
 * Where the panel is. Carries no display copy: an `error` message is the service's own text ('' when it gave none);
 * every other state's copy belongs to the consuming component.
 */
export type TrackingLinkPhase =
  | { kind: 'loading' }
  /** This version has no link yet. */
  | { kind: 'idle' }
  /** Making it. */
  | { kind: 'working' }
  | { kind: 'done'; link: TrackingLink }
  | { kind: 'no-brand' }
  /** The version's link could not be read. */
  | { kind: 'load-error'; message: string }
  /** The link could not be made. */
  | { kind: 'error'; message: string };

export interface UseTrackingLink {
  phase: TrackingLinkPhase;
  /** Make the version's link (minting its `aff_id`), or update its name and destination when it exists (keeping it). */
  create: (destination: TrackingLinkDestination) => Promise<TrackingLink | null>;
  /** Move the shown link to another destination (same `aff_id`). Serialised, so the last pick is the one that stays;
   *  resolves false when there is no shown link or the move failed (the link is then left as it was). */
  retarget: (destination: TrackingLinkDestination) => Promise<boolean>;
  /** Read the version's link again (e.g. once the landing page it lands on went live, for its address). */
  reload: () => Promise<void>;
  /** Show a link another call answered (e.g. a quick landing page, which points the version's link at itself). */
  adopt: (link: TrackingLink) => void;
}

/** Whether a link already lands where `destination` points: a Studio page by its id, anything else by its URL. Pure. */
export const sameLinkDestination = (
  link: Pick<TrackingLink, 'destination_type' | 'landing_page_id' | 'destination_url'>,
  destination: TrackingLinkDestination,
): boolean => {
  if (link.destination_type !== destination.destination_type) return false;
  if (destination.landing_page_id) return link.landing_page_id === destination.landing_page_id;
  if (link.landing_page_id) return false;
  return sameDestination(link.destination_url, destination.destination_url);
};

/**
 * One asset version's tracking link, minted and stored by spark-creatives. On mount it reads the asset's links and
 * shows this version's straight away; with none, `idle` (the panel's "Get tracking link"). Pure of copy and layout.
 * The composer keys the panel by version, so each version gets a fresh read.
 */
export function useTrackingLink({
  tenantId,
  creativeId,
  versionKey,
  kind,
  name,
}: UseTrackingLinkInput): UseTrackingLink {
  const [phase, setPhaseState] = React.useState<TrackingLinkPhase>({ kind: 'loading' });
  // The phase as last set, readable by a queued retarget before React re-renders.
  const phaseRef = React.useRef<TrackingLinkPhase>(phase);
  const mountedRef = React.useRef(true);
  const setPhase = React.useCallback((next: TrackingLinkPhase): void => {
    phaseRef.current = next;
    if (mountedRef.current) setPhaseState(next);
  }, []);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Only the newest read lands.
  const loadSeqRef = React.useRef(0);
  const load = React.useCallback(async (): Promise<void> => {
    const seq = ++loadSeqRef.current;
    if (!tenantId) {
      setPhase({ kind: 'no-brand' });
      return;
    }
    const res = await fetchTrackingLinks(tenantId, { creativeId });
    if (seq !== loadSeqRef.current) return;
    if (!res.ok) {
      if (res.aborted) return;
      // A failed re-read keeps the link already shown; only a read with nothing shown yet ends in the error.
      if (phaseRef.current.kind !== 'done') setPhase({ kind: 'load-error', message: res.error ?? '' });
      return;
    }
    const row = res.data.find((l) => l.version_key === versionKey);
    setPhase(row ? { kind: 'done', link: row } : { kind: 'idle' });
  }, [tenantId, creativeId, versionKey, setPhase]);

  // Syncs with the network: the version's link, read once per brand / asset / version.
  React.useEffect(() => {
    void load();
  }, [load]);

  const create = React.useCallback(
    async (destination: TrackingLinkDestination): Promise<TrackingLink | null> => {
      if (!tenantId) {
        setPhase({ kind: 'no-brand' });
        return null;
      }
      loadSeqRef.current += 1; // a read still in flight must not replace what this answers
      setPhase({ kind: 'working' });
      const res = await upsertTrackingLink(tenantId, {
        creative_id: creativeId,
        version_key: versionKey,
        kind,
        name,
        destination_type: destination.destination_type,
        ...(destination.landing_page_id ? { landing_page_id: destination.landing_page_id } : {}),
        destination_url: destination.destination_url,
      });
      if (!res.ok) {
        setPhase({ kind: 'error', message: res.error ?? '' });
        return null;
      }
      invalidateBrandLinks(tenantId);
      setPhase({ kind: 'done', link: res.data });
      return res.data;
    },
    [tenantId, creativeId, versionKey, kind, name, setPhase],
  );

  // Retargets run one after another, so two quick picks never land out of order on the link.
  const chainRef = React.useRef<Promise<unknown>>(Promise.resolve());
  const retarget = React.useCallback(
    (destination: TrackingLinkDestination): Promise<boolean> => {
      const job = chainRef.current.then(async (): Promise<boolean> => {
        const current = phaseRef.current;
        if (current.kind !== 'done' || !tenantId) return false;
        // Already there (e.g. a link reopened on the page it lands on): nothing to write.
        if (sameLinkDestination(current.link, destination)) return true;
        const res = await retargetTrackingLink(tenantId, current.link.id, destination);
        if (!res.ok) return false;
        invalidateBrandLinks(tenantId);
        // Onto the phase as it is NOW (a reload may have replaced it meanwhile).
        const latest = phaseRef.current;
        if (latest.kind === 'done' && latest.link.id === res.data.id) setPhase({ kind: 'done', link: res.data });
        return true;
      });
      chainRef.current = job.catch(() => false);
      return job;
    },
    [tenantId, setPhase],
  );

  const adopt = React.useCallback(
    (link: TrackingLink): void => {
      loadSeqRef.current += 1;
      if (tenantId) invalidateBrandLinks(tenantId);
      setPhase({ kind: 'done', link });
    },
    [tenantId, setPhase],
  );

  return { phase, create, retarget, reload: load, adopt };
}

export default useTrackingLink;
